import makeAuthenticatedRequest from '../base-api';
import {
  GENERATE_AGENT_INVOICES_ASYNC,
  GENERATE_CLIENT_INVOICES_ASYNC,
  GENERATE_CUSTOM_AGENT_INVOICES_ASYNC,
  GET_GENERATION_JOB_STATUS,
  LIST_GENERATION_JOBS,
} from '../../endpoints';

interface ApiError extends Error {
  status?: number;
  data?: any;
}

const parseResponse = async <T = any>(response: Response): Promise<T> => {
  const result = await response.json();

  if (!response.ok) {
    const error = new Error(
      result?.message || `HTTP error! status: ${response.status}`
    ) as ApiError;
    error.status = response.status;
    error.data = result;
    throw error;
  }

  return result;
};

// Types
export interface GenerationJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  statusUrl: string;
  estimatedAgents?: number;
  estimatedClients?: number;
  estimatedTime?: string;
  period: {
    month?: string;
    monthName?: string;
    startDate?: string;
    endDate?: string;
    daysInMonth?: number;
    totalDays?: number;
  };
  filter?: {
    type?: 'all' | 'specific_agents' | 'specific_clients' | string;
    agentIds?: string[];
  };
}

export interface JobProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  percentage: number;
  currentEntity?: string;
}

export interface JobStatus {
  _id?: string;  // MongoDB ID
  jobId?: string; // Our custom job ID
  status: 'pending' | 'processing' | 'completed' | 'failed';
  invoiceType: 'agent' | 'client';
  // Period can be either an object or at root level
  month?: number;  // Root level month
  year?: number;   // Root level year
  startDate?: string;
  endDate?: string;
  period?: string | {  // Can be a string like "October 2025" or an object
    month?: number;
    year?: number;
    monthName?: string;
    startDate?: string;
    endDate?: string;
    totalDays?: number;
  };
  requestedBy?: {
    name: string;
    email: string;
  };
  progress?: JobProgress;
  summary?: {
    totalProcessed?: number;
    successfullyGenerated?: number;
    errors?: number;
    totalInvoiceAmount?: number;
  };
  results?: {
    generatedInvoices?: any[];
    errors?: any[];
    totalAmount?: number;
  };
  invoices?: any[];
  errors?: any[];
  timing?: {
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
    duration?: number;
  };
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  processingStartedAt?: string;
  processingCompletedAt?: string;
  processingDuration?: number;
  workerId?: string;
  totalAmount?: number;
  emailNotification?: {
    sent: boolean;
    recipients?: string[];
    sentAt?: string;
  };
  metadata?: {
    regeneration?: boolean;
    agentIds?: string[];
    clientIds?: string[];
    isFilteredGeneration?: boolean;
    filterType?: 'all' | 'specific_agents' | 'specific_clients';
  };
}

export interface GenerationJobsResponse {
  jobs: JobStatus[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface MonthGenerationStatus {
  month: number;
  monthName: string;
  lastJob?: JobStatus;
  hasGenerated: boolean;
  canRegenerate: boolean;
}

export interface CustomAgentGenerationPayload {
  startDate: string;
  endDate: string;
  agentIds?: string[];
  regenerate?: boolean;
}

// Start async agent invoice generation
export const startAgentInvoiceGeneration = async (
  year: number,
  month: number,
  options?: { regenerate?: boolean; notes?: string }
): Promise<GenerationJob> => {
  try {
    const url = new URL(GENERATE_AGENT_INVOICES_ASYNC(year, month));
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const response = await makeAuthenticatedRequest(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const result = await parseResponse<{ data: GenerationJob }>(response);

    return result.data;
  } catch (error: any) {
    console.error('Start agent invoice generation failed:', error);
    throw error;
  }
};

// Start async agent invoice generation with custom date range
export const startCustomAgentInvoiceGeneration = async (
  payload: CustomAgentGenerationPayload
): Promise<GenerationJob> => {
  try {
    const response = await makeAuthenticatedRequest(
      GENERATE_CUSTOM_AGENT_INVOICES_ASYNC,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const result = await parseResponse<{ data: GenerationJob }>(response);

    return result.data;
  } catch (error: any) {
    console.error('Start custom agent invoice generation failed:', error);
    throw error;
  }
};

// Start async client/brand invoice generation
export const startClientInvoiceGeneration = async (
  year: number,
  month: number,
  options?: { regenerate?: boolean; notes?: string; clientIds?: string[] }
): Promise<GenerationJob> => {
  try {
    const url = new URL(GENERATE_CLIENT_INVOICES_ASYNC(year, month));
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && key !== 'clientIds') {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const payload: Record<string, any> = {};
    if (options?.clientIds && options.clientIds.length > 0) {
      payload.clientIds = options.clientIds;
    }

    const response = await makeAuthenticatedRequest(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await parseResponse<{ data: GenerationJob }>(response);

    return result.data;
  } catch (error: any) {
    console.error('Start client invoice generation failed:', error);
    throw error;
  }
};

// Get generation job status
export const getGenerationJobStatus = async (jobId: string): Promise<JobStatus> => {
  try {
    const response = await makeAuthenticatedRequest(GET_GENERATION_JOB_STATUS(jobId), {
      method: 'GET',
    });

    const result = await parseResponse<{ data: JobStatus }>(response);

    return result.data;
  } catch (error: any) {
    console.error('Get generation job status failed:', error);
    throw error;
  }
};

// List all generation jobs
export const listGenerationJobs = async (filters?: {
  status?: string;
  invoiceType?: string;
  year?: number;
  month?: number;
  page?: number;
  limit?: number;
}): Promise<GenerationJobsResponse> => {
  try {
    const url = new URL(LIST_GENERATION_JOBS());
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const response = await makeAuthenticatedRequest(url.toString(), {
      method: 'GET',
    });

    const result = await parseResponse(response);

    // Check if the response has jobs array directly or wrapped in data
    const jobs = result.data?.jobs || result.jobs || result.data || result || [];
    const pagination = result.data?.pagination || result.pagination;

    // Return in expected format
    return {
      jobs: Array.isArray(jobs) ? jobs : [],
      pagination: pagination || { page: 1, limit: 100, total: 0, pages: 1 }
    };
  } catch (error: any) {
    console.error('List generation jobs failed:', error);
    throw error;
  }
};

// Get generation history for a specific period
export const getGenerationHistory = async (
  year: number,
  month: number,
  invoiceType: 'agent' | 'client' = 'agent'
) => {
  try {
    const url = new URL(`/invoice/generation/history/${year}/${month}`);
    url.searchParams.append('invoiceType', invoiceType);

    const response = await makeAuthenticatedRequest(url.toString(), {
      method: 'GET',
    });

    const result = await parseResponse<{ data: any }>(response);

    return result.data;
  } catch (error: any) {
    console.error('Get generation history failed:', error);
    throw error;
  }
};
